/**
 * Customer Table Configuration
 * Reusable styles and constants for the customer list table
 */

import type { SxProps, Theme } from '@mui/material';
import type { ColumnConfig } from '../types/customerTable.types';

// Table column configuration with API field mappings
/**
 * Table column configuration
 * sortField values match CustomerSortField type in customer.ts (camelCase)
 */
export const TABLE_COLUMNS: ColumnConfig[] = [
  {
    id: 'customer',
    label: 'Customer Name',
    sortable: true,
    width: 'auto',
    sortField: 'name',
  },
  {
    id: 'trucks',
    label: 'Trucks',
    sortable: true,
    width: '60px',
    sortField: 'truckCount',
  },
  {
    id: 'dueWithin2Weeks',
    label: '2 Weeks',
    sortable: true,
    width: '100px',
    sortField: 'trucksDueWithin2Weeks',
  },
  {
    id: 'dueWithin2To6Weeks',
    label: '2-6 Weeks',
    sortable: true,
    width: '100px',
    sortField: 'trucksDueWithin2To6Weeks',
  },
  {
    id: 'dueWithin6To12Weeks',
    label: '6-12 Weeks',
    sortable: true,
    width: '120px',
    sortField: 'trucksDueWithin6To12Weeks',
  },
  {
    id: 'actions',
    label: '',
    sortable: false,
    width: '40px',
  },
];

// Shared style configurations
export const STYLES = {
  // Search TextField - Responsive width
  searchField: {
    width: { xs: '100%', sm: 400, md: 500 },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f9fafb',
      borderRadius: 1,
      fontSize: '0.875rem',
      '& fieldset': {
        borderColor: '#e5e7eb',
      },
      '&:hover fieldset': {
        borderColor: '#d1d5db',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3b82f6',
        borderWidth: 1,
      },
    },
    '& input': {
      padding: '8px 12px',
      fontSize: '0.875rem',
    },
    '& input::placeholder': {
      fontSize: '0.8125rem',
      opacity: 0.7,
    },
  } as SxProps<Theme>,

  // New Customer Button - Responsive sizing
  newCustomerButton: {
    minWidth: { xs: 'auto', sm: 150 },
    width: { xs: '100%', sm: 'auto' },
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    backgroundColor: '#3b82f6',
    padding: { xs: '10px 16px', sm: '7px 16px' },
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#2563eb',
      boxShadow: 'none',
    },
  } as SxProps<Theme>,

  // Table cell - header
  tableCellHeader: {
    fontWeight: 600,
    py: 1.75,
    px: 1.5,
    fontSize: '0.875rem',
    color: '#374151',
    borderBottom: '1px solid #e5e7eb',
  } as SxProps<Theme>,

  // Sortable header cell (clickable)
  tableCellHeaderSortable: {
    fontWeight: 600,
    py: 1.75,
    px: 1.5,
    fontSize: '0.875rem',
    color: '#374151',
    borderBottom: '1px solid #e5e7eb',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.15s ease-in-out',
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
  } as SxProps<Theme>,

  // Active sort column header
  tableCellHeaderActive: {
    backgroundColor: '#eff6ff',
    color: '#1e40af',
    fontWeight: 700,
  } as SxProps<Theme>,

  // Sort indicator icon
  sortIcon: {
    ml: 0.75,
    fontSize: '0.875rem',
    color: '#9ca3af',
    transition: 'color 0.15s ease-in-out',
  } as SxProps<Theme>,

  // Active sort indicator
  sortIconActive: {
    color: '#3b82f6',
    fontWeight: 700,
  } as SxProps<Theme>,

  // Table cell - body
  tableCellBody: {
    py: 2,
    px: 2.5,
    borderBottom: '1px solid #f3f4f6',
  } as SxProps<Theme>,

  // Table row hover
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f9fafb',
    },
    transition: 'background-color 0.15s ease-in-out',
  } as SxProps<Theme>,

  // Avatar
  avatar: {
    width: 40,
    height: 40,
    bgcolor: '#111827',
    fontSize: '0.75rem',
    fontWeight: 600,
  } as SxProps<Theme>,

  // Customer name
  customerName: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#111827',
    mb: 0.25,
  } as SxProps<Theme>,

  // Customer email
  customerEmail: {
    fontSize: '0.75rem',
    color: '#6b7280',
    display: 'block',
  } as SxProps<Theme>,

  // Customer address
  customerAddress: {
    fontSize: '0.7rem',
    color: '#9ca3af',
    display: 'block',
    mt: 0.25,
    fontStyle: 'italic',
  } as SxProps<Theme>,

  // Notes chip
  notesChip: {
    maxWidth: 250,
    fontSize: '0.75rem',
    height: 24,
    borderColor: '#e5e7eb',
    color: '#6b7280',
    '& .MuiChip-label': {
      px: 1.5,
    },
  } as SxProps<Theme>,

  // Delete button
  deleteButton: {
    backgroundColor: '#ef4444',
    color: 'white',
    width: 36,
    height: 36,
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#dc2626',
    },
  } as SxProps<Theme>,
};

// Constants
export const ITEMS_PER_PAGE = 10;
export const SKELETON_ROWS = 8;
